"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import {
  Activity,
  AlertOctagon,
  Bell,
  BellRing,
  Bot,
  Check,
  CheckCheck,
  ChevronRight,
  CircleDot,
  ExternalLink,
  GitPullRequestArrow,
  LoaderCircle,
  Radio,
  RefreshCw,
  Settings2,
  ShieldAlert,
  Sparkles,
  TriangleAlert,
  X,
} from "lucide-react";
import { useAlerts } from "../contexts/AlertContext";
import {
  fetchAgentSentinelNotifications,
  fetchNotifications,
  markNotificationActed,
  markNotificationDismissed,
  markNotificationRead,
} from "../services/api";
import {
  browserPushCapability,
  disableBrowserPush,
  enableBrowserPush,
  inspectBrowserPush,
} from "../notifications/browserPush";
import { isSafeNotificationUrl } from "../notifications/notificationData";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui";
import "./nexus-notifications.css";

type NotificationAction = {
  type?: "external" | "navigate";
  label?: string;
  targetView?: string;
  url?: string | null;
  agentId?: string | null;
};

type HqsNotification = {
  id: string;
  backendId?: number | null;
  title: string;
  message: string;
  category: string;
  categoryLabel?: string;
  priority: "info" | "medium" | "high" | "critical";
  createdAt: string;
  read: boolean;
  actedAt?: string | null;
  dismissedAt?: string | null;
  requiresAction?: boolean;
  source?: string;
  sourceLabel?: string;
  reason?: string | null;
  action?: NotificationAction;
  metadata?: Record<string, unknown>;
};

type FeedState = "loading" | "live" | "degraded" | "offline";
type PushState = "loading" | "unsupported" | "unconfigured" | "blocked" | "available" | "subscribed" | "error";
type FilterId = "all" | "action" | "critical" | "agents" | "system";

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: "all", label: "Alle" },
  { id: "action", label: "Aktion" },
  { id: "critical", label: "Kritisch" },
  { id: "agents", label: "Agenten" },
  { id: "system", label: "System" },
];

const AGENT_CATEGORIES = new Set(["agent", "pr", "approval"]);

function formatRelativeTime(value: string) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Zeit unbekannt";
  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const absolute = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat("de", { numeric: "auto" });
  if (absolute < 60) return formatter.format(seconds, "second");
  if (absolute < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (absolute < 86_400) return formatter.format(Math.round(seconds / 3600), "hour");
  return formatter.format(Math.round(seconds / 86_400), "day");
}

function priorityLabel(priority: HqsNotification["priority"]) {
  if (priority === "critical") return "SOFORT";
  if (priority === "high") return "HOCH";
  if (priority === "medium") return "PRÜFEN";
  return "INFO";
}

function NotificationIcon({ notification }: { notification: HqsNotification }) {
  if (notification.category === "pr") return <GitPullRequestArrow />;
  if (notification.category === "approval") return <ShieldAlert />;
  if (notification.category === "agent") return <Bot />;
  if (notification.priority === "critical") return <AlertOctagon />;
  if (notification.priority === "high") return <TriangleAlert />;
  return <Activity />;
}

function mapSentinelNotifications(data: unknown) {
  const payload = data as { summary?: { latest?: Array<Record<string, unknown>> } } | null;
  const latest = Array.isArray(payload?.summary?.latest) ? payload.summary.latest : [];
  return latest.map((issue) => ({
    source: "backend_sentinel",
    sourceId: `issue:${String(issue.id || "unknown")}:${String(issue.status || "open")}:${String(issue.escalationLevel || "none")}`,
    title: String(issue.title || "Sentinel-Ereignis"),
    message: `${String(issue.area || "System")} · Status ${String(issue.status || "offen").replaceAll("_", " ")}`,
    category: String(issue.status || "").includes("approval") ? "approval" : "pipeline",
    priority: issue.severity === "critical" || issue.escalationLevel === "critical" ? "critical" : issue.severity === "high" ? "high" : "medium",
    createdAt: typeof issue.createdAt === "string" ? issue.createdAt : new Date().toISOString(),
    targetView: "operations",
    requiresAction: issue.status === "waiting_approval" || issue.severity === "critical",
    metadata: { issueId: issue.id, escalationLevel: issue.escalationLevel },
  }));
}

function filterNotifications(items: HqsNotification[], filter: FilterId) {
  if (filter === "action") return items.filter((item) => item.requiresAction && !item.actedAt);
  if (filter === "critical") return items.filter((item) => item.priority === "critical" || item.priority === "high");
  if (filter === "agents") return items.filter((item) => AGENT_CATEGORIES.has(item.category));
  if (filter === "system") return items.filter((item) => !AGENT_CATEGORIES.has(item.category));
  return items;
}

export default function NexusNotificationCenter({
  onNavigate,
}: {
  onNavigate: (targetView: string, notification: HqsNotification) => void;
}) {
  const {
    alerts,
    alertPreferences,
    toastAlerts,
    summary,
    ingestNotifications,
    markAsRead,
    markAllAsRead,
    markAsActed,
    dismissAlert,
    dismissToast,
    updatePreferences,
  } = useAlerts();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterId>("all");
  const [feedState, setFeedState] = useState<FeedState>("loading");
  const [feedMessage, setFeedMessage] = useState("Railway-Ereignisse werden synchronisiert");
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [pushState, setPushState] = useState<PushState>("loading");
  const [pushMessage, setPushMessage] = useState("Push-Status wird geprüft");
  const [pushBusy, setPushBusy] = useState(false);
  const initialSyncRef = useRef(true);

  const syncNotifications = useCallback(async () => {
    setSyncing(true);
    const [feedResult, sentinelResult] = await Promise.allSettled([
      fetchNotifications(150),
      fetchAgentSentinelNotifications(),
    ]);

    const incoming: unknown[] = [];
    let successfulSources = 0;
    if (feedResult.status === "fulfilled" && !feedResult.value.error && Array.isArray(feedResult.value.data?.notifications)) {
      incoming.push(...feedResult.value.data.notifications.map((item: Record<string, unknown>) => ({ ...item, source: "backend" })));
      successfulSources += 1;
    }
    if (sentinelResult.status === "fulfilled" && !sentinelResult.value.error && sentinelResult.value.data) {
      incoming.push(...mapSentinelNotifications(sentinelResult.value.data));
      successfulSources += 1;
    }

    if (incoming.length > 0) {
      ingestNotifications(incoming, { announce: !initialSyncRef.current });
    }
    initialSyncRef.current = false;
    const now = new Date().toISOString();
    setLastSyncAt(now);
    if (successfulSources === 2) {
      setFeedState("live");
      setFeedMessage("Railway Store + Sentinel live");
    } else if (successfulSources === 1) {
      setFeedState("degraded");
      setFeedMessage("Ein Backend-Kanal ist erreichbar");
    } else {
      setFeedState("offline");
      setFeedMessage("Backend-Feed nicht erreichbar · lokale Ereignisse bleiben aktiv");
    }
    setSyncing(false);
  }, [ingestNotifications]);

  useEffect(() => {
    void syncNotifications();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void syncNotifications();
    }, 30_000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void syncNotifications();
    };
    const handleRefresh = () => void syncNotifications();
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleRefresh);
    window.addEventListener("hqs:notification-refresh", handleRefresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleRefresh);
      window.removeEventListener("hqs:notification-refresh", handleRefresh);
    };
  }, [syncNotifications]);

  const refreshPushState = useCallback(async () => {
    const capability = browserPushCapability();
    if (!capability.supported) {
      setPushState("unsupported");
      setPushMessage("Dieser Browser unterstützt Web Push nicht");
      return;
    }
    const state = await inspectBrowserPush();
    if (state.permission === "denied") {
      setPushState("blocked");
      setPushMessage("Im Browser blockiert");
    } else if (!state.configured) {
      setPushState("unconfigured");
      setPushMessage("Push-Zustellung ist serverseitig noch nicht vollständig bestätigt");
    } else if (state.subscribed) {
      setPushState("subscribed");
      setPushMessage("Gerät registriert · Railway-Zustellung bestätigt");
    } else {
      setPushState("available");
      setPushMessage("Bereit zur Aktivierung");
    }
  }, []);

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  useEffect(() => {
    const timers = (toastAlerts as HqsNotification[])
      .filter((item) => item.priority === "info" || item.priority === "medium")
      .map((item) => window.setTimeout(() => dismissToast(item.id), item.priority === "info" ? 7_000 : 10_000));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [dismissToast, toastAlerts]);

  const activeAlerts = useMemo(
    () => (alerts as HqsNotification[]).filter((item) => !item.dismissedAt),
    [alerts],
  );
  const filteredAlerts = useMemo(
    () => filterNotifications(activeAlerts, filter),
    [activeAlerts, filter],
  );

  const markRead = useCallback((notification: HqsNotification) => {
    markAsRead(notification.id);
    if (notification.backendId) void markNotificationRead(notification.backendId);
  }, [markAsRead]);

  const handleMarkAllRead = useCallback(() => {
    const backendIds = activeAlerts.filter((item) => !item.read && item.backendId).map((item) => item.backendId as number);
    markAllAsRead();
    void Promise.allSettled(backendIds.map((id) => markNotificationRead(id)));
  }, [activeAlerts, markAllAsRead]);

  const handleDismiss = useCallback((event: MouseEvent, notification: HqsNotification) => {
    event.stopPropagation();
    dismissAlert(notification.id);
    if (notification.backendId) void markNotificationDismissed(notification.backendId);
  }, [dismissAlert]);

  const openNotification = useCallback((notification: HqsNotification) => {
    markAsActed(notification.id);
    dismissToast(notification.id);
    if (notification.backendId) void markNotificationActed(notification.backendId, "opened_from_notification_center");
    const url = notification.action?.url;
    if (url && isSafeNotificationUrl(url, window.location.origin)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    onNavigate(notification.action?.targetView || "pulse", notification);
    setOpen(false);
  }, [dismissToast, markAsActed, onNavigate]);

  const togglePush = useCallback(async () => {
    setPushBusy(true);
    try {
      if (pushState === "subscribed") {
        await disableBrowserPush();
        updatePreferences({ browserPush: false });
      } else {
        await enableBrowserPush();
        updatePreferences({ browserPush: true });
      }
      await refreshPushState();
    } catch (error) {
      setPushState("error");
      setPushMessage(error instanceof Error ? error.message : "Push-Aktivierung fehlgeschlagen");
    } finally {
      setPushBusy(false);
    }
  }, [pushState, refreshPushState, updatePreferences]);

  const urgency = summary.critical > 0 ? "critical" : summary.actionRequired > 0 ? "action" : summary.unread > 0 ? "new" : "quiet";

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className={`notification-trigger notification-trigger--${urgency}`}
            aria-label={`${summary.unread} ungelesene Benachrichtigungen`}
          >
            <span className="notification-trigger__radar" aria-hidden="true" />
            {urgency === "critical" ? <BellRing /> : <Bell />}
            {summary.unread > 0 ? <strong>{summary.unread > 99 ? "99+" : summary.unread}</strong> : null}
          </button>
        </SheetTrigger>
        <SheetContent className="notification-sheet" aria-label="HQS Notification & Escalation Center">
          <div className="notification-sheet__aurora" aria-hidden="true"><i /><i /><i /></div>
          <SheetHeader className="notification-sheet__header">
            <div className="notification-sheet__eyebrow"><Radio /> NOTIFICATION & ESCALATION LAYER</div>
            <SheetTitle>Was deine Aufmerksamkeit <em>jetzt</em> braucht.</SheetTitle>
            <SheetDescription>Backend-Ereignisse, Agentenresultate, PRs und Freigaben – priorisiert, nachvollziehbar und direkt ausführbar.</SheetDescription>
          </SheetHeader>

          <section className={`notification-command-state notification-command-state--${urgency}`}>
            <div className="notification-command-state__orb"><span /><i>{summary.critical > 0 ? <AlertOctagon /> : <BellRing />}</i></div>
            <div>
              <span>ATTENTION STATE</span>
              <strong>{summary.critical > 0 ? `${summary.critical} kritisch` : summary.actionRequired > 0 ? `${summary.actionRequired} Aktion${summary.actionRequired === 1 ? "" : "en"}` : summary.unread > 0 ? `${summary.unread} neu` : "Lage ruhig"}</strong>
              <small>{feedMessage}</small>
            </div>
            <button type="button" onClick={() => void syncNotifications()} disabled={syncing} aria-label="Benachrichtigungen aktualisieren"><RefreshCw className={syncing ? "nexus-spin" : ""} /></button>
          </section>

          <div className="notification-metrics" aria-label="Benachrichtigungsübersicht">
            <span><small>UNGELESEN</small><strong>{summary.unread}</strong></span>
            <span><small>AKTION</small><strong>{summary.actionRequired}</strong></span>
            <span><small>KRITISCH</small><strong>{summary.critical}</strong></span>
            <span className={`notification-feed-state notification-feed-state--${feedState}`}><small>FEED</small><strong>{feedState.toUpperCase()}</strong></span>
          </div>

          <div className="notification-filter-row">
            <div role="tablist" aria-label="Benachrichtigungen filtern">
              {FILTERS.map((item) => (
                <button key={item.id} type="button" role="tab" aria-selected={filter === item.id} className={filter === item.id ? "active" : ""} onClick={() => setFilter(item.id)}>{item.label}</button>
              ))}
            </div>
            {summary.unread > 0 ? <button type="button" onClick={handleMarkAllRead}><CheckCheck /> Alle gelesen</button> : null}
          </div>

          <section className="notification-stream" aria-label="Meldungsverlauf">
            {filteredAlerts.length === 0 ? (
              <div className="notification-empty"><span><Sparkles /></span><strong>Keine Meldungen in diesem Kanal.</strong><small>Neue Backend- und Agentenereignisse erscheinen hier automatisch.</small></div>
            ) : filteredAlerts.map((notification) => (
              <article
                key={notification.id}
                className={`notification-card notification-card--${notification.priority} ${notification.read ? "is-read" : "is-unread"}`}
              >
                <button type="button" className="notification-card__main" onClick={() => markRead(notification)}>
                  <span className="notification-card__icon"><NotificationIcon notification={notification} /></span>
                  <span className="notification-card__copy">
                    <span className="notification-card__meta"><b>{priorityLabel(notification.priority)}</b><i>{notification.categoryLabel || notification.category}</i><time dateTime={notification.createdAt}>{formatRelativeTime(notification.createdAt)}</time></span>
                    <strong>{notification.title}</strong>
                    <small>{notification.message}</small>
                    <span className="notification-card__truth"><CircleDot /> {notification.sourceLabel || "HQS Status"}{notification.reason ? ` · ${notification.reason}` : ""}</span>
                  </span>
                  {!notification.read ? <i className="notification-card__new" aria-label="Neu" /> : null}
                </button>
                <div className="notification-card__actions">
                  <button type="button" onClick={() => openNotification(notification)}>{notification.action?.label || "Prüfen"}{notification.action?.url ? <ExternalLink /> : <ChevronRight />}</button>
                  <button type="button" aria-label="Meldung ausblenden" onClick={(event) => handleDismiss(event, notification)}><X /></button>
                </div>
              </article>
            ))}
          </section>

          <section className="notification-settings">
            <div className="notification-settings__heading"><span><Settings2 /> Zustellung</span><small>{lastSyncAt ? `Letzter Sync ${formatRelativeTime(lastSyncAt)}` : "Noch nicht synchronisiert"}</small></div>
            <div className={`notification-push notification-push--${pushState}`}>
              <span>{pushState === "subscribed" ? <Check /> : pushBusy ? <LoaderCircle className="nexus-spin" /> : <BellRing />}</span>
              <div><strong>Browser-Push für kritische Ereignisse</strong><small>{pushMessage}</small></div>
              <button type="button" onClick={() => void togglePush()} disabled={pushBusy || ["unsupported", "unconfigured", "blocked", "loading"].includes(pushState)}>{pushState === "subscribed" ? "Deaktivieren" : "Aktivieren"}</button>
            </div>
            <div className="notification-rule-row">
              <label><span>In-App Einblendungen</span><input type="checkbox" checked={alertPreferences.inAppAlerts !== false} onChange={(event) => updatePreferences({ inAppAlerts: event.target.checked })} /></label>
              <label><span>Agenten & PRs</span><input type="checkbox" checked={alertPreferences.agentAlerts !== false} onChange={(event) => updatePreferences({ agentAlerts: event.target.checked })} /></label>
              <label><span>System & Pipeline</span><input type="checkbox" checked={alertPreferences.systemAlerts !== false} onChange={(event) => updatePreferences({ systemAlerts: event.target.checked })} /></label>
            </div>
          </section>
        </SheetContent>
      </Sheet>

      <section className="notification-toast-stack" aria-live="polite" aria-label="Neue HQS Meldungen">
        {(toastAlerts as HqsNotification[]).map((notification) => (
          <article key={notification.id} className={`notification-toast notification-toast--${notification.priority}`}>
            <span className="notification-toast__beam" aria-hidden="true" />
            <span className="notification-toast__icon"><NotificationIcon notification={notification} /></span>
            <div><span>{priorityLabel(notification.priority)} · {notification.sourceLabel}</span><strong>{notification.title}</strong><small>{notification.message}</small><button type="button" onClick={() => openNotification(notification)}>{notification.action?.label || "Jetzt prüfen"}<ChevronRight /></button></div>
            <button type="button" aria-label="Einblendung schließen" onClick={() => dismissToast(notification.id)}><X /></button>
          </article>
        ))}
      </section>
    </>
  );
}
